import React, { useEffect, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FormInputDate } from '../../../../../form-components/FormInputDate';
import { Box, Typography, Button, MenuItem, TextField, Modal, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import { getLockerArea, updateLocker, updateLockerReset, moveLocker } from '../../../../../api/ticket';
import dayjs from 'dayjs';
import { errorMessages } from '../../../../../utils/methods';
import {
  getMemberByBranch,
  assignLockerPassSpot,
  applyLockerPassPlan,
} from '../../../../../api/member-management';
import LockerAttachDateModal from '../../../../common/modal/LockerAttachDateModal';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ko } from 'date-fns/locale';
import QueryString from 'qs';
import ConfirmModal from '../../../../common/modal/ConfirmModal';
import PeriodChangePreviewModal from '../../../../common/modal/PeriodChangePreviewModal';
import { buildLockerShiftPlan } from '../../../../../utils/lockerShiftPlan';
import AlertModal from '../../../../common/modal/AlertModal';
import LockerSelectModal from '../../../../SalesManagment/modal/enterSaleInfoModal/lockerSelectModal/LockerSelectModal';
import CommonModalButton from '../../../../../form-components/CommonModalButton';
import LockerSpotLogTab from './LockerSpotLogTab';


import {
  ModalContainer,
  ModalHeader,
  ModalTitle,
  StyledCloseIcon,
  ModalContent,
  ModalContentNoPadTop,
  FormRow,
  FormRowLast,
  FormLabel,
  FormInputRow,
  MemberInfoRow,
  MemberInfoLeft,
  MemberAvatar,
  MemberName,
  MemberPhone,
  MemberDetailLink,
  StyledTextField,
  DisabledTextField,
  StyledSelect,
  StyledSwitch,
  StyledRadio,
  StyledAutocomplete,
  DateSeparator,
  PlaceholderText,
  LockerMoveButton,
  ModalFooter,
  ModalFooterAssign,
  EndUsageButton,
  CancelButton,
  SubmitButton,
  ErrorFieldWrapper,
  MemoTextarea,
} from './LockerUpdateModalStyled';
import { useNavigate } from 'react-router-dom';
import { getBranchProductsById } from '../../../../../api/point';
import { getGeneralProducts } from '../../../../../api/user';

// 이용권 드롭다운의 고정 옵션 — 회원 보유 패스가 아니라 상품 기반 무료 직접배정을 뜻한다.
const FREE_ASSIGN_VALUE = 'FREE_ASSIGN';

// 이력 탭은 컬럼이 많아 모달을 넓힌다(정보 탭은 기존 520px 유지)
const HISTORY_MODAL_SX = { width: 'min(960px, calc(100vw - 32px))' };

const LockerUpdateModal = ({
  memberList,
  open,
  setOpen,
  setLockerDetailSingle,
  lockerDetailSingle,
  branchId,
  branchIdData,
  centerIdData,
  // '+ 연장 추가' — 판매등록 연장 프리셋으로 넘기는 콜백 (라커관리가 마운트한 판매등록 모달)
  onExtend,
}) => {
  const [searchResults, setSearchResults] = useState([]);
  const [memberData, setMemberData] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [memberType, setMemberType] = useState('registered');
  const [lockerArea, setLockerArea] = useState('');
  const [selectedTicket, setSelectedTicket] = useState('');
  // '무료 배정' 선택 시에만 쓰는 지점 라커 상품 (기존 직접배정 플로우)
  const [selectedFreeProduct, setSelectedFreeProduct] = useState('');
  const [unregisteredName, setUnregisteredName] = useState('');
  const [unregisteredPhone, setUnregisteredPhone] = useState('');
  const [memo, setMemo] = useState('');
  const [password, setPassword] = useState('');
  const [initialPassword, setInitialPassword] = useState('');
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [showEndSuccess, setShowEndSuccess] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  // 라커이동에서 본인 자리+기간 겹침일 때의 '이어서 배정' 대상 자리와 시작일
  const [moveAttachLocker, setMoveAttachLocker] = useState(null);
  const [moveAttachPasses, setMoveAttachPasses] = useState([]);
  const [periodPreview, setPeriodPreview] = useState(null);
  const [errorFields, setErrorFields] = useState({});
  // 자리 상세 탭 — 'info'(기존 정보) | 'history'(자리 사용 이력, #165)
  const [activeTab, setActiveTab] = useState('info');

  const { t } = useTranslation();
  const user = useSelector(state => state.user.user);
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // 다른 자리를 열 때마다 정보 탭부터 보여준다
  useEffect(() => {
    if (open) setActiveTab('info');
  }, [open, lockerDetailSingle?.lockerId]);

  // React Query - 락커 구역 목록 조회
  const { data: lockerAreas = [] } = useQuery({
    queryKey: ['lockerAreas', user?.branch?.branchId],
    queryFn: () => getLockerArea(user?.branch?.branchId, user.token),
    enabled: !!user?.branch?.branchId && !!user?.token && open,
    onSuccess: data => {
      if (data?.length > 0 && !lockerArea) {
        setLockerArea(data[0].id);
      }
    },
    onError: error => {
      console.log('getLockerAreaDatas 오류 : ', error);
    },
  });

  const { data: lockerTicketList } = useQuery({
    queryKey: ['ticketList', branchId],
    queryFn: () => getBranchProductsById(user.token, branchId, 'page=1&limit=1000000'),
    enabled: !!branchId && !!user?.token,
  });

  // 락커 카테고리 상품만 필터링
  const lockerProducts = useMemo(() => {
    if (!lockerTicketList?.content) return [];
    return lockerTicketList.content.filter(product => product.productCategoryType === 'LOCKER');
  }, [lockerTicketList]);

  // React Query - 락커 수정 mutation
  const updateMutation = useMutation({
    mutationFn: data => updateLocker(lockerDetailSingle.lockerId, user.token, data),
    onSuccess: (_, variables) => {
      // 미배정 라커에서 회원/이용권/기간 없이 메모만 보낸 경우 → "메모 저장" 메시지로 분기.
      const isMemoOnly =
        !isAssigned &&
        !variables.memberId &&
        !variables.unregisteredName &&
        !variables.branchProductId &&
        !variables.expireDate;
      if (isMemoOnly) {
        toast.success(t('메모가 저장되었습니다'));
      } else {
        toast.success(isAssigned ? t('라커가 수정되었습니다') : t('라커가 배정되었습니다'));
      }
      queryClient.invalidateQueries({ queryKey: ['lockers', branchId || branchIdData] });
      setOpen(false);
    },
    onError: error => {
      toast.error(errorMessages(error));
    },
  });

  // React Query - 사용 여부 토글 전용 mutation
  const toggleActiveMutation = useMutation({
    mutationFn: data => updateLocker(lockerDetailSingle.lockerId, user.token, data),
    onError: error => {
      toast.error(errorMessages(error));
    },
  });

  // React Query - 락커 사용 종료 mutation
  const resetMutation = useMutation({
    mutationFn: () => updateLockerReset(lockerDetailSingle.lockerId, user.token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lockers', branchId || branchIdData] });
      setShowEndConfirm(false);
      setShowEndSuccess(true);
    },
    onError: error => {
      toast.error(errorMessages(error));
    },
  });

  // React Query - 락커 이동 mutation
  const moveMutation = useMutation({
    mutationFn: targetLockerId => moveLocker(lockerDetailSingle.lockerId, targetLockerId, user.token),
    onSuccess: () => {
      toast.success(t('락커가 이동되었습니다'));
      queryClient.invalidateQueries({ queryKey: ['lockers', branchId || branchIdData] });
      closeModal();
    },
    onError: error => {
      toast.error(errorMessages(error));
    },
  });

  // 전화번호 포맷팅 함수 (010-0000-0000)
  const formatPhoneNumber = value => {
    const numbers = value.replace(/[^0-9]/g, '');
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    } else {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
    }
  };

  // 회원이 이미 배정되어 있는지 확인
  // 등록 회원 또는 미등록 회원이 배정된 경우
  const isAssigned =
    lockerDetailSingle?.memberId || lockerDetailSingle?.memberName || lockerDetailSingle?.unregisteredMemberName;

  const defaultValues = useMemo(() => {
    return {
      expireDate: lockerDetailSingle.expireDate || '',
      issueDate: lockerDetailSingle.issueDate || dayjs().format('YYYY-MM-DD'),
      memberId: lockerDetailSingle.memberId || '',
    };
  }, [lockerDetailSingle, open]);

  const {
    handleSubmit,
    control,
    setError,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues,
  });

  // 신규 배정: 선택한 회원이 보유한 미배정 라커 패스 (회원상세 일반상품 API 재사용)
  const watchedFormMemberId = watch('memberId');
  const { data: memberGeneralProducts } = useQuery({
    queryKey: ['generalProducts', watchedFormMemberId, branchId],
    queryFn: () => getGeneralProducts(watchedFormMemberId, user.token, branchId),
    enabled: !!watchedFormMemberId && !!user?.token && !!branchId && open && !isAssigned,
  });
  const unassignedPasses = useMemo(() => {
    return (memberGeneralProducts?.lockerPasses ?? []).filter(
      lp => !lp.branchLockerId && lp.status !== 'INACTIVE'
    );
  }, [memberGeneralProducts]);
  const selectedPass = useMemo(
    () => unassignedPasses.find(lp => lp.memberLockerPassId === selectedTicket) || null,
    [unassignedPasses, selectedTicket]
  );

  // 수정 모달: 이 자리에 링크된 패스들 — 캐시(MIN~MAX)로 합쳐 보이던 것을 패스별로 펼친다.
  // 사이에 공백이 있어도 각 카드의 실제 기간이 그대로 보이고, 각각 기간 수정이 가능하다.
  const { data: assignedMemberProducts, isLoading: spotPassesLoading } = useQuery({
    queryKey: ['generalProducts', lockerDetailSingle?.memberId, branchId],
    queryFn: () => getGeneralProducts(lockerDetailSingle.memberId, user.token, branchId),
    enabled: !!isAssigned && !!lockerDetailSingle?.memberId && !!user?.token && !!branchId && open,
  });
  const spotPasses = useMemo(() => {
    return (assignedMemberProducts?.lockerPasses ?? [])
      .filter(lp => lp.branchLockerId === lockerDetailSingle?.lockerId)
      .sort((a, b) => (a.startDate || '').localeCompare(b.startDate || ''));
  }, [assignedMemberProducts, lockerDetailSingle]);

  // 패스별 기간 편집 상태 — {passId: {start: Date, expire: Date}}
  const [passDateEdits, setPassDateEdits] = useState({});
  // 기간 편집 대상으로 선택된 행 — 기본은 첫 번째 이용권 (하나면 그 하나)
  const [editingPassId, setEditingPassId] = useState(null);
  useEffect(() => {
    if (open && isAssigned && spotPasses.length > 0) {
      setEditingPassId(prev =>
        prev && spotPasses.some(lp => lp.memberLockerPassId === prev) ? prev : spotPasses[0].memberLockerPassId
      );
    }
    // 닫힐 때 리셋 — 다음에 열면 다시 첫 행이 선택된다 (onSuccess 는 closeModal 을 안 거친다)
    if (!open) {
      setEditingPassId(null);
      setPassDateEdits({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isAssigned, spotPasses]);
  const passEditOf = lp => passDateEdits[lp.memberLockerPassId] || {};
  const passStartValue = lp =>
    passEditOf(lp).start !== undefined ? passEditOf(lp).start : dayjs(lp.startDate).toDate();
  const passExpireValue = lp =>
    passEditOf(lp).expire !== undefined ? passEditOf(lp).expire : dayjs(lp.expireDate).toDate();
  const setPassEdit = (lp, key, date) =>
    setPassDateEdits(prev => ({
      ...prev,
      [lp.memberLockerPassId]: { ...passEditOf(lp), [key]: date },
    }));
  // 시작일만 오늘로 옮기고 이용 일수는 그대로 유지한다.
  // 종료일을 고정하면 회원이 산 기간이 조용히 줄어든다 (회원상세 기간 변경과 같은 규칙).
  const setPassStartToday = lp => {
    const start = passStartValue(lp);
    const expire = passExpireValue(lp);
    const today = dayjs().startOf('day');
    const span = start && expire ? dayjs(expire).startOf('day').diff(dayjs(start).startOf('day'), 'day') : null;
    setPassDateEdits(prev => ({
      ...prev,
      [lp.memberLockerPassId]: {
        start: today.toDate(),
        expire: span === null ? passExpireValue(lp) : today.add(span, 'day').toDate(),
      },
    }));
  };

  // 손댄 기간을 원래 값으로 되돌린다
  const resetPassEdit = lp =>
    setPassDateEdits(prev => {
      const next = { ...prev };
      delete next[lp.memberLockerPassId];
      return next;
    });

  const passEditChanged = lp =>
    !dayjs(passStartValue(lp)).isSame(dayjs(lp.startDate), 'day') ||
    !dayjs(passExpireValue(lp)).isSame(dayjs(lp.expireDate), 'day');

  const passStatusLabel = lp => {
    if (lp.status === 'STOPPED') return { text: '중지', color: '#EE735A', bg: 'rgba(238,115,90,0.12)' };
    const today = dayjs().startOf('day');
    if (lp.status !== 'ACTIVE' || dayjs(lp.expireDate).isBefore(today, 'day')) {
      return { text: '만료', color: '#999', bg: '#F0F0F0' };
    }
    if (dayjs(lp.startDate).isAfter(today, 'day')) {
      return { text: '사용예정', color: '#F0A030', bg: 'rgba(240,160,48,0.12)' };
    }
    return { text: '이용중', color: '#1F3A5F', bg: 'rgba(31, 58, 95,0.10)' };
  };

  // 이용권 선택 시 기간 자동 설정 (새로운 배정일 때만)
  useEffect(() => {
    // 이미 배정된 락커(수정 모드)는 기존 날짜 유지
    if (isAssigned) return;

    // 회원 보유 패스 선택 — 패스의 기간을 그대로 표시 (수정은 회원상세에서)
    if (selectedPass) {
      setValue('issueDate', selectedPass.startDate);
      setValue('expireDate', selectedPass.expireDate);
      return;
    }

    // 무료 배정 — 선택한 지점 상품의 기간으로 계산
    const freeProductId = selectedTicket === FREE_ASSIGN_VALUE ? selectedFreeProduct : null;
    if (!freeProductId || !lockerProducts.length) return;

    const selectedProduct = lockerProducts.find(product => product.productId === freeProductId);
    if (!selectedProduct || !selectedProduct.periodValue || !selectedProduct.periodType) return;

    const today = dayjs();
    let expireDate;

    switch (selectedProduct.periodType) {
      case 'MONTH':
        expireDate = today.add(selectedProduct.periodValue, 'month').subtract(1, 'day');
        break;
      case 'WEEK':
        expireDate = today.add(selectedProduct.periodValue, 'week').subtract(1, 'day');
        break;
      case 'DAY':
        expireDate = today.add(selectedProduct.periodValue, 'day').subtract(1, 'day');
        break;
      case 'YEAR':
        expireDate = today.add(selectedProduct.periodValue, 'year').subtract(1, 'day');
        break;
      default:
        expireDate = today.add(selectedProduct.periodValue, 'month').subtract(1, 'day');
    }

    setValue('issueDate', today.format('YYYY-MM-DD'));
    setValue('expireDate', expireDate.format('YYYY-MM-DD'));
  }, [selectedTicket, selectedFreeProduct, selectedPass, lockerProducts, setValue, isAssigned]);

  // 필드 값 변경 시 해당 에러 제거
  const watchedIssueDate = watch('issueDate');
  const watchedExpireDate = watch('expireDate');

  // 시작 날짜 변경 시 종료 날짜 자동 업데이트
  useEffect(() => {
    // 이미 배정된 락커(수정 모드)는 기존 날짜 유지, 패스 선택 시에도 기간 고정
    if (isAssigned || selectedPass) return;
    const freeProductId = selectedTicket === FREE_ASSIGN_VALUE ? selectedFreeProduct : null;
    if (!freeProductId || !lockerProducts.length || !watchedIssueDate) return;

    const selectedProduct = lockerProducts.find(product => product.productId === freeProductId);
    if (!selectedProduct || !selectedProduct.periodValue || !selectedProduct.periodType) return;

    const startDate = dayjs(watchedIssueDate);
    if (!startDate.isValid()) return;

    let newExpireDate;

    switch (selectedProduct.periodType) {
      case 'MONTH':
        newExpireDate = startDate.add(selectedProduct.periodValue, 'month').subtract(1, 'day');
        break;
      case 'WEEK':
        newExpireDate = startDate.add(selectedProduct.periodValue, 'week').subtract(1, 'day');
        break;
      case 'DAY':
        newExpireDate = startDate.add(selectedProduct.periodValue, 'day').subtract(1, 'day');
        break;
      case 'YEAR':
        newExpireDate = startDate.add(selectedProduct.periodValue, 'year').subtract(1, 'day');
        break;
      default:
        newExpireDate = startDate.add(selectedProduct.periodValue, 'month').subtract(1, 'day');
    }

    setValue('expireDate', newExpireDate.format('YYYY-MM-DD'));
  }, [watchedIssueDate, selectedTicket, selectedFreeProduct, selectedPass, lockerProducts, setValue]);

  useEffect(() => {
    setErrorFields(prev => {
      const updated = { ...prev };
      if (watchedIssueDate) delete updated.issueDate;
      if (watchedExpireDate) delete updated.expireDate;
      if (memberData) delete updated.member;
      if (unregisteredName) delete updated.unregisteredName;
      if (selectedTicket) delete updated.ticket;
      if (selectedFreeProduct) delete updated.freeProduct;
      return updated;
    });
  }, [watchedIssueDate, watchedExpireDate, memberData, unregisteredName, selectedTicket, selectedFreeProduct]);

  // 회원 검색 함수
  const getMembers = async inputValue => {
    if (!inputValue || inputValue.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const qstring = QueryString.stringify({
        searchInput: inputValue,
        headBranchId: centerIdData,
        branchId: branchIdData,
      });

      const data = await getMemberByBranch(user.token, qstring);
      const searchResultsArray = data?.content.map(item => ({
        id: item.memberId,
        name: `${item.name} (${item.phoneNumber || ''})`,
        memberData: item,
      }));
      setSearchResults(searchResultsArray);
    } catch (error) {
      toast.error(errorMessages(error));
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // 회원 선택 핸들러
  const handleMemberSelect = (event, value) => {
    if (value) {
      setMemberData(value.memberData);
      setValue('memberId', value.memberData.memberId);
    } else {
      setMemberData(null);
      setValue('memberId', '');
    }
  };

  const submit = async (values, periodConfirmed = false, confirmedEntries = null) => {
    const errors = {};
    const { expireDate, issueDate, memberId } = values;

    // 사용 여부가 false일 때는 유효성 검사 건너뛰고 바로 저장
    if (!isActive) {
      const data = {
        useYn: 'N',
        lockerAreaId: lockerDetailSingle.lockerAreaId,
        lockerId: lockerDetailSingle.lockerId,
      };
      updateMutation.mutate(data);
      return;
    }

    // 수정 모달 — 이용권 스택에서 바꾼 기간을 하단 저장에서 함께 반영한다
    if (isAssigned) {
      const changedPasses = spotPasses.filter(passEditChanged);
      for (const lp of changedPasses) {
        const start = passStartValue(lp);
        const expire = passExpireValue(lp);
        if (!dayjs(start).isValid() || !dayjs(expire).isValid()) {
          toast.error(t('날짜를 확인해주세요'));
          return;
        }
        if (dayjs(expire).isBefore(dayjs(start), 'day')) {
          toast.error(t('종료일이 시작일보다 빠릅니다'));
          return;
        }
      }
      // 한 건만 고쳤으면 같은 자리 뒤 이용권과의 관계를 회원상세와 같은 규칙으로 따진다.
      // 여러 건을 한 번에 고쳤다면 운영자가 이미 직접 배치를 잡은 것으로 보고 그대로 저장한다.
      let shiftPlan = null;
      if (changedPasses.length === 1) {
        const target = changedPasses[0];
        shiftPlan = buildLockerShiftPlan({
          spotPasses,
          passId: target.memberLockerPassId,
          startDate: passStartValue(target),
          expireDate: passExpireValue(target),
        });
        if (shiftPlan?.blockedBy) {
          toast.error(
            shiftPlan.blockedKind === 'stopped'
              ? t('중지 중인 이용권 기간({{period}})과 겹칩니다', { period: shiftPlan.blockedBy })
              : t('이미 지난 이용 기록({{period}})과 겹칩니다', { period: shiftPlan.blockedBy })
          );
          return;
        }
        if (!shiftPlan?.shiftedCount) shiftPlan = null;
      }

      // 기간이 바뀌면 상태(이용중/사용예정/만료)도 함께 뒤집힌다. 회원상세와 같은 창으로 먼저 보여준다.
      // react-hook-form 의 handleSubmit 은 (values, event) 로 부른다.
      // 느슨하게 판정하면 event 가 truthy 라 확인 단계를 건너뛴다 — 반드시 true 인지 본다.
      if (changedPasses.length > 0 && periodConfirmed !== true) {
        setPeriodPreview({
          values,
          shifted: shiftPlan?.shifted ?? [],
          entries: shiftPlan?.entries ?? null,
          items: changedPasses.map(lp => ({
            key: lp.memberLockerPassId,
            productName: lp.productName,
            before: { startDate: lp.startDate, expireDate: lp.expireDate, status: lp.status },
            after: { startDate: passStartValue(lp), expireDate: passExpireValue(lp), status: lp.status },
          })),
        });
        return;
      }

      try {
        // 배치로 한 번에 확정한다. 하나씩 저장하면 세 번째에서 겹침으로 거부됐을 때
        // 앞 두 건은 이미 저장돼 되돌릴 수 없다. 서버는 최종 상태만 검증한다.
        await applyLockerPassPlan(
          user.token,
          lockerDetailSingle.memberId,
          lockerDetailSingle.lockerId,
          confirmedEntries ??
            changedPasses.map(lp => ({
              memberLockerPassId: lp.memberLockerPassId,
              startDate: dayjs(passStartValue(lp)).format('YYYY-MM-DD'),
              expireDate: dayjs(passExpireValue(lp)).format('YYYY-MM-DD'),
            }))
        );
      } catch (error) {
        // 같은 자리 다른 패스와 겹치면 서버가 406 으로 거부한다 — 라커 저장도 중단
        toast.error(errorMessages(error));
        return;
      }
      if (changedPasses.length > 0) {
        queryClient.invalidateQueries({ queryKey: ['generalProducts'] });
      }
    }

    // 미배정 라커에서 "메모만 입력" 케이스 — 회원/이용권/만료일 어느 것도 손대지 않았으면
    // 라커 자체에 대한 메모만 저장한다 (회원 배정 없이도 메모를 남길 수 있어야 함).
    const hasAssignmentIntent =
      isAssigned ||
      (memberType === 'registered' && (memberData || memberId)) ||
      (memberType === 'unregistered' && (unregisteredName || unregisteredPhone)) ||
      !!selectedTicket ||
      !!expireDate;

    if (!hasAssignmentIntent) {
      const memoOnlyData = {
        useYn: 'Y',
        lockerAreaId: lockerDetailSingle.lockerAreaId,
        lockerId: lockerDetailSingle.lockerId,
        memo: memo || '',
      };
      // 비밀번호: 사용자가 실제로 수정한 경우에만 전송 (미전송 시 백엔드는 기존값 유지).
      if (password !== initialPassword) {
        memoOnlyData.password = password;
      }
      updateMutation.mutate(memoOnlyData);
      return;
    }

    // 회원 배정 의도가 감지되면 기존 검증 그대로 적용
    if (!isAssigned) {
      if (memberType === 'registered') {
        if (!memberId) errors.member = true;
        if (!selectedTicket) errors.ticket = true;
        if (selectedTicket === FREE_ASSIGN_VALUE && !selectedFreeProduct) errors.freeProduct = true;
      } else if (memberType === 'unregistered' && !unregisteredName) {
        errors.unregisteredName = true;
      }
    }
    // 수정 모달은 이용권/기간을 패스 카드에서 개별 저장하므로 여기서 검증하지 않는다.

    // 날짜 검증 — 수정 모달은 기간을 이용권 스택에서 다루므로 제외.
    if (!isAssigned) {
      if (!issueDate) errors.issueDate = true;
      if (!expireDate) errors.expireDate = true;
    }

    // 에러가 있으면 표시하고 리턴
    if (Object.keys(errors).length > 0) {
      setErrorFields(errors);

      // 첫 번째 에러에 대한 메시지 표시
      const errorMsgs = {
        member: '회원을 선택해주세요',
        ticket: '이용권을 선택해주세요',
        freeProduct: '무료 배정할 상품을 선택해주세요',
        unregisteredName: '이름을 입력해주세요',
        issueDate: '시작일자를 입력해주세요',
        expireDate: '종료일자를 입력해주세요',
      };
      const firstError = Object.keys(errors)[0];
      toast.error(errorMsgs[firstError]);
      return;
    }

    // 회원 보유 패스 선택 — 자리 링크를 거는 배정 API 를 사용한다.
    // 기간도 이 한 번의 호출로 함께 넘긴다. 기간 저장과 배정을 나눠 부르면 배정이 겹침으로 거부됐을 때
    // 기간만 바뀐 중간 상태가 남고, 토스트는 겹침 오류만 띄워 운영자가 그걸 알 수 없다.
    if (!isAssigned && memberType === 'registered' && selectedPass) {
      try {
        const dateChanged =
          !dayjs(issueDate).isSame(dayjs(selectedPass.startDate), 'day') ||
          !dayjs(expireDate).isSame(dayjs(selectedPass.expireDate), 'day');
        await assignLockerPassSpot(
          user.token,
          memberId,
          selectedPass.memberLockerPassId,
          lockerDetailSingle.lockerId,
          dateChanged
            ? {
                startDate: dayjs(issueDate).format('YYYY-MM-DD'),
                expireDate: dayjs(expireDate).format('YYYY-MM-DD'),
              }
            : undefined
        );
        // 메모·비밀번호는 자리(row) 속성이라 변경분만 기존 API 로 저장
        const lockerExtras = {};
        if (memo !== (lockerDetailSingle.memo || '')) lockerExtras.memo = memo;
        if (password !== initialPassword) lockerExtras.password = password;
        if (Object.keys(lockerExtras).length > 0) {
          await updateLocker(lockerDetailSingle.lockerId, user.token, {
            useYn: isActive ? 'Y' : 'N',
            lockerAreaId: lockerDetailSingle.lockerAreaId,
            lockerId: lockerDetailSingle.lockerId,
            ...lockerExtras,
          });
        }
        toast.success(t('라커가 배정되었습니다'));
        queryClient.invalidateQueries({ queryKey: ['lockers', branchId || branchIdData] });
        queryClient.invalidateQueries({ queryKey: ['generalProducts'] });
        setOpen(false);
      } catch (error) {
        toast.error(errorMessages(error));
      }
      return;
    }

    const data = {
      useYn: isActive ? 'Y' : 'N',
      lockerAreaId: lockerDetailSingle.lockerAreaId,
      lockerId: lockerDetailSingle.lockerId,
    };

    // 날짜가 유효할 때만 포함 — 수정 모달은 기간을 패스 카드에서 저장하므로 보내지 않는다
    // (자리 캐시는 패스 기준으로 재계산되는 값이라 직접 수정하지 않는다)
    if (!isAssigned && issueDate && dayjs(issueDate).isValid()) {
      data.issueDate = dayjs(issueDate).format('YYYY-MM-DD');
    }
    if (!isAssigned && expireDate && dayjs(expireDate).isValid()) {
      data.expireDate = dayjs(expireDate).format('YYYY-MM-DD');
    }

    // memberId 설정
    if (isAssigned) {
      data.memberId = lockerDetailSingle.memberId;
    } else if (memberType === 'registered' && memberId) {
      data.memberId = memberId;
    } else if (memberType === 'unregistered') {
      if (unregisteredName) data.unregisteredName = unregisteredName;
      if (unregisteredPhone) data.unregisteredPhone = unregisteredPhone;
    }

    // branchProductId — 신규 배정의 무료 배정에서만 의미가 있다 (백엔드 직접배정 트리거)
    if (!isAssigned && selectedTicket === FREE_ASSIGN_VALUE && selectedFreeProduct) {
      data.branchProductId = selectedFreeProduct;
    }

    // memberLockerPassId가 있을 때만 포함 (기존 배정 수정 시)
    if (lockerDetailSingle.memberLockerPassId) {
      data.memberLockerPassId = lockerDetailSingle.memberLockerPassId;
    }

    // 메모 (백엔드 필드 추가 후 정상 저장됨)
    data.memo = memo || '';
    // 비밀번호: 변경된 경우에만 전송. 빈 문자열이면 서버에서 삭제 처리.
    if (password !== initialPassword) {
      data.password = password;
    }

    console.log('=== 락커 요청 ===');
    console.log('lockerDetailSingle:', lockerDetailSingle);
    console.log('lockerProducts:', lockerProducts);
    console.log('selectedTicket:', selectedTicket);
    console.log('update data:', data);

    updateMutation.mutate(data);
  };

  const handleEndUsageClick = () => {
    setShowEndConfirm(true);
  };

  const handleEndUsageConfirm = () => {
    resetMutation.mutate();
  };

  const onMoveUserDetail = memberId => {
    navigate(`/member/${memberId}`);
  };

  const handleEndSuccessClose = () => {
    setShowEndSuccess(false);
    closeModal();
  };

  // 스택에서 선택한(편집 대상) 이용권 — 라커 이동은 이 이용권 1개만 옮긴다 (회원상세와 동일 계약)
  const selectedSpotPass = spotPasses.find(lp => lp.memberLockerPassId === editingPassId) || spotPasses[0];

  // 이동 후 원래 자리에 남은 만료 이용권은 자리에서 떼어 미배정으로 둔다.
  // 그대로 두면 그 자리가 계속 연체처럼 보이고 다음 배정의 겹침 검증에도 걸린다.
  const isExpiredPass = lp =>
    lp.status === 'INACTIVE' ||
    (lp.expireDate && dayjs(lp.expireDate).isBefore(dayjs().startOf('day'), 'day'));

  // 선택한 이용권들을 대상 자리로 링크 이동. entry 에 기간이 있으면 그 기간으로 바꿔서 옮긴다.
  const moveSelectedPasses = async (targetLockerId, entries) => {
    for (const entry of entries) {
      await assignLockerPassSpot(
        user.token,
        lockerDetailSingle.memberId,
        entry.memberLockerPassId,
        targetLockerId,
        entry.startDate && entry.expireDate
          ? {
              startDate: dayjs(entry.startDate).format('YYYY-MM-DD'),
              expireDate: dayjs(entry.expireDate).format('YYYY-MM-DD'),
            }
          : undefined
      );
    }
    toast.success(t('락커가 이동되었습니다'));
    queryClient.invalidateQueries({ queryKey: ['lockers', branchId || branchIdData] });
    queryClient.invalidateQueries({ queryKey: ['generalProducts'] });
    closeModal();
  };

  // 락커 이동 핸들러
  const handleLockerMove = async (selectedLocker, passIds = []) => {
    if (!selectedLocker || !selectedLocker.lockerId) return;
    if (selectedLocker.lockerId === lockerDetailSingle.lockerId) return;
    // 링크 패스가 없는 레거시 자리는 기존 자리 단위 이동으로 폴백
    if (!selectedSpotPass) {
      try {
        await moveMutation.mutateAsync(selectedLocker.lockerId);
      } catch (error) {
        return false;
      }
      return;
    }
    // 옮길 이용권들 — 체크박스로 고른 것(없으면 선택된 이용권 하나).
    // 만료도 함께 옮긴다. 연체 정보는 자리가 아니라 회원에게 붙는 것이라 회원을 따라가야 한다.
    // 단 기간은 고정이라 합치기 큐에서 재배치되지 않는다(expired 표시로 잠긴다).
    const movingPasses = (passIds.length > 0
      ? spotPasses.filter(lp => passIds.includes(lp.memberLockerPassId))
      : [selectedSpotPass]
    ).filter(Boolean).map(lp => ({ ...lp, expired: isExpiredPass(lp) }));

    // 본인이 쓰는 자리로 합칠 때는 겹침 여부·개수와 무관하게 '합치기' 확인 창으로 유도 —
    // 기존 종료일 다음 날부터 이용 일수를 유지하며 순서대로 이어 붙인다(수정 가능).
    const isOwnSpot = selectedLocker.memberId === lockerDetailSingle.memberId;
    if (isOwnSpot) {
      setMoveAttachLocker(selectedLocker);
      setMoveAttachPasses(movingPasses);
      setShowMoveModal(false);
      return;
    }
    // 빈 자리로 옮길 때는 기간 그대로 이동한다.
    try {
      await moveSelectedPasses(selectedLocker.lockerId, movingPasses.map(lp => ({
        memberLockerPassId: lp.memberLockerPassId,
      })));
    } catch (error) {
      // 토스트 후 false 반환으로 자리 선택 모달을 유지해 재선택 가능
      toast.error(errorMessages(error));
      return false;
    }
  };

  const closeMoveAttach = () => {
    setMoveAttachLocker(null);
    setMoveAttachPasses([]);
  };

  // '이어서 배정' 확정 — 모달이 계산한 기간과 자리를 한 번에 반영한다.
  const confirmMoveAttach = async entries => {
    if (!moveAttachLocker || !entries?.length) return;
    try {
      await applyLockerPassPlan(
        user.token,
        lockerDetailSingle.memberId,
        moveAttachLocker.lockerId,
        entries.map(entry => ({
          memberLockerPassId: entry.memberLockerPassId,
          startDate: dayjs(entry.startDate).format('YYYY-MM-DD'),
          expireDate: dayjs(entry.expireDate).format('YYYY-MM-DD'),
        }))
      );
      toast.success(t('락커가 이동되었습니다'));
      queryClient.invalidateQueries({ queryKey: ['lockers', branchId || branchIdData] });
      queryClient.invalidateQueries({ queryKey: ['generalProducts'] });
      closeMoveAttach();
      closeModal();
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  // 사용 여부 토글 핸들러
  const handleUseYnToggle = e => {
    const newValue = e.target.checked;
    setIsActive(newValue);

    const data = {
      isActive: newValue,
      lockerAreaId: lockerDetailSingle.lockerAreaId,
      lockerId: lockerDetailSingle.lockerId,
    };

    toggleActiveMutation.mutate(data, {
      onSuccess: () => {
        toast.success(newValue ? t('라커가 활성화되었습니다') : t('라커가 비활성화되었습니다'));
        queryClient.invalidateQueries({ queryKey: ['lockers', branchId || branchIdData] });
        // 비활성화 시에만 모달 닫기
        if (!newValue) {
          closeModal();
        }
      },
    });
  };

  const closeModal = () => {
    reset(defaultValues);
    setLockerDetailSingle({});
    setMemberData(null);
    setUnregisteredName('');
    setUnregisteredPhone('');
    setMemberType('registered');
    setSelectedTicket('');
    setSelectedFreeProduct('');
    setLockerArea('');
    setMemo('');
    setPassword('');
    setInitialPassword('');
    setShowEndConfirm(false);
    setShowEndSuccess(false);
    setShowMoveModal(false);
    setEditingPassId(null);
    setPassDateEdits({});
    setErrorFields({});
    setOpen(false);
  };

  useEffect(() => {
    reset(defaultValues);
    if (lockerDetailSingle.memberId && memberList.length > 0) {
      const existingMember = memberList.find(member => member.value === lockerDetailSingle.memberId);
      if (existingMember) {
        setMemberData({
          memberId: existingMember.value,
          name: existingMember.label,
        });
        setMemberType('registered');
      }
    }
    // 이미 할당된 상품이 있으면 selectedTicket에 설정
    if (lockerDetailSingle?.branchProduct?.id) {
      setSelectedTicket(lockerDetailSingle.branchProduct.id);
    }
    // 락커 구역 설정
    if (lockerDetailSingle?.lockerAreaId) {
      setLockerArea(lockerDetailSingle.lockerAreaId);
    }
    // useYn에 따라 사용 여부 설정
    if (lockerDetailSingle?.useYn !== undefined) {
      setIsActive(lockerDetailSingle.useYn === 'Y');
    }
    // 기존 메모가 있으면 로드, 없으면 빈 값
    setMemo(lockerDetailSingle?.memo || '');
    // 기존 비밀번호 로드 + 초기값 스냅샷 (변경 여부 판단용)
    const loadedPassword = lockerDetailSingle?.password || '';
    setPassword(loadedPassword);
    setInitialPassword(loadedPassword);
  }, [memberList, lockerDetailSingle]);

  const tabBar = (
    <Box sx={{ display: 'flex', gap: '20px', px: '24px', borderBottom: '1px solid #ECEFF0', flexShrink: 0 }}>
      {[
        ['info', t('정보')],
        ['history', t('이력')],
      ].map(([key, label]) => (
        <Box
          key={key}
          component="button"
          type="button"
          onClick={() => setActiveTab(key)}
          sx={{
            border: 'none', background: 'none', cursor: 'pointer', p: '12px 2px',
            fontSize: 14, fontWeight: activeTab === key ? 700 : 500,
            color: activeTab === key ? '#1F3A5F' : '#737373',
            borderBottom: `2px solid ${activeTab === key ? '#1F3A5F' : 'transparent'}`,
            mb: '-1px',
          }}>
          {label}
        </Box>
      ))}
    </Box>
  );

  const historyView = (
    <>
      <ModalContent sx={{ pt: '16px' }}>
        <LockerSpotLogTab lockerId={lockerDetailSingle?.lockerId} />
      </ModalContent>
      <ModalFooterAssign>
        <CommonModalButton variant="cancel" onClick={closeModal}>
          {t('닫기')}
        </CommonModalButton>
      </ModalFooterAssign>
    </>
  );

  const isLoading =
    updateMutation.isPending || resetMutation.isPending || moveMutation.isPending || toggleActiveMutation.isPending;

  // 회원이 이미 배정된 경우 - 수정 모달
  if (isAssigned) {
    return (
      <>
        <Modal open={open} onClose={closeModal} disableEscapeKeyDown>
          <ModalContainer sx={activeTab === 'history' ? HISTORY_MODAL_SX : undefined}>
            {/* Header — 라커 번호를 제목으로 승격 (디자인 최종안 05) */}
            <ModalHeader>
              <ModalTitle>
                {t('라커 정보')} · {lockerDetailSingle.lockerNumber}
                {t('번')}
              </ModalTitle>
              <StyledCloseIcon onClick={closeModal} />
            </ModalHeader>

            {/* 회원 + 현재 상태 헤더 밴드, 라커 이동은 명시적 액션 버튼 */}
            <Box
              sx={{
                background: '#F5F7FA',
                borderBottom: '1px solid #ECEFF0',
                p: '14px 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0,
              }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <MemberAvatar>
                  <PersonIcon sx={{ color: '#999' }} />
                </MemberAvatar>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#262626' }}>
                      {lockerDetailSingle.memberName}
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        fontSize: 11, fontWeight: 600, color: isActive ? '#4A6A94' : '#B0573E',
                        border: `1px solid ${isActive ? '#4A6A94' : '#B0573E'}`,
                        borderRadius: '999px', p: '1px 8px',
                      }}>
                      {isActive ? t('사용중') : t('사용안함')}
                    </Box>
                  </Box>
                  <Typography sx={{ fontSize: 13, color: '#666', mt: '2px' }}>
                    {lockerDetailSingle.memberPhone} ·{' '}
                    <Box
                      component="span"
                      onClick={() => onMoveUserDetail(lockerDetailSingle.memberId)}
                      sx={{ color: '#1F3A5F', fontWeight: 500, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                      {t('회원 상세')}
                    </Box>
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="outlined"
                onClick={() => setShowMoveModal(true)}
                sx={{
                  height: 34, px: '12px', border: '1px solid #1F3A5F', borderRadius: '8px',
                  color: '#1F3A5F', fontSize: 13, fontWeight: 600, background: '#fff',
                  '&:hover': { border: '1px solid #172C48', background: 'rgba(31, 58, 95,0.04)' },
                }}
                startIcon={
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="#1F3A5F">
                    <path d="M6.99 11L3 15l3.99 4v-3H14v-2H6.99v-3zM21 9l-3.99-4v3H10v2h7.01v3L21 9z" />
                  </svg>
                }>
                {t('라커 이동')}
              </Button>
            </Box>

            {tabBar}

            {activeTab === 'history' ? historyView : (
            <>
            {/* Content */}
            <ModalContent sx={{ pt: '8px' }}>

              {/* 이용권 스택 — 상태 뱃지 + 실제 기간(공백 그대로 노출). 행을 클릭해 선택하면
                  아래 분리된 박스에서 그 이용권의 기간을 편집한다. */}
              <FormRow sx={{ alignItems: 'flex-start' }}>
                <FormLabel sx={{ pt: 1 }}>{t('이용권')}</FormLabel>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ border: '1px solid #d9d9d9', background: '#fff', borderRadius: '8px', overflow: 'hidden' }}>
                    {/* 불러오는 동안 '없습니다'가 잠깐 비치지 않게 로딩을 먼저 보인다 */}
                    {spotPassesLoading && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: '10px 12px' }}>
                        <CircularProgress size={14} sx={{ color: '#1F3A5F' }} />
                        <Typography sx={{ fontSize: 13, color: '#999' }}>{t('불러오는 중')}</Typography>
                      </Box>
                    )}
                    {!spotPassesLoading && spotPasses.length === 0 && (
                      <Typography sx={{ fontSize: 13, color: '#999', p: '10px 12px' }}>
                        {t('연결된 이용권이 없습니다')}
                      </Typography>
                    )}
                    {spotPasses.map((lp, idx) => {
                      const label = passStatusLabel(lp);
                      const isEditing = editingPassId === lp.memberLockerPassId;
                      return (
                        <Box
                          key={lp.memberLockerPassId}
                          onClick={() => setEditingPassId(lp.memberLockerPassId)}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 1, p: '10px 12px', cursor: 'pointer',
                            borderTop: idx > 0 ? '1px solid #ececec' : 'none',
                            // 선택 행 — 테두리로 무엇을 편집 중인지 표시
                            boxShadow: isEditing ? 'inset 0 0 0 2px #1F3A5F' : 'none',
                            background: isEditing ? 'rgba(74,179,188,0.08)' : 'transparent',
                            '&:hover': { background: isEditing ? 'rgba(74,179,188,0.08)' : '#F3F5F9' },
                          }}>
                          <Box
                            component="span"
                            sx={{
                              fontSize: 11, fontWeight: 700, color: label.color,
                              background: label.bg, borderRadius: '5px', p: '2px 7px', flexShrink: 0,
                            }}>
                            {t(label.text)}
                          </Box>
                          <Typography sx={{ flex: 1, fontSize: 13, fontWeight: isEditing ? 700 : 600, color: '#262626' }}>
                            {lp.productName || t('라커 이용권')}
                          </Typography>
                          <Typography sx={{ fontSize: 12.5, color: '#737373' }}>
                            {dayjs(lp.startDate).format('YYYY.MM.DD')} ~ {dayjs(lp.expireDate).format('YYYY.MM.DD')}
                          </Typography>
                        </Box>
                      );
                    })}
                    {onExtend && (
                      <Box
                        onClick={() => onExtend(lockerDetailSingle)}
                        sx={{
                          p: '9px 12px', borderTop: '1px dashed #d9d9d9', textAlign: 'center',
                          fontSize: 12.5, fontWeight: 600, color: '#1F3A5F', cursor: 'pointer',
                          '&:hover': { background: 'rgba(74,179,188,0.06)' },
                        }}>
                        + {t('연장 추가')}
                      </Box>
                    )}
                  </Box>
                </Box>
              </FormRow>

              {/* 선택한 이용권의 기간 변경 — 기존 기간 설정과 같은 위치·폭 */}
              {spotPasses
                .filter(lp => lp.memberLockerPassId === editingPassId)
                .map(lp => (
                  <FormRow key={lp.memberLockerPassId} sx={{ alignItems: 'flex-start' }}>
                    <FormLabel sx={{ pt: '8px' }}>{t('기간 변경')}</FormLabel>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                    <FormInputRow>
                      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
                        <Box sx={{ flex: 1, '& > *': { width: '100%' } }}>
                          <DatePicker
                            inputFormat="yyyy-MM-dd"
                            value={passStartValue(lp)}
                            onChange={date => setPassEdit(lp, 'start', date)}
                            renderInput={params => <StyledTextField size="small" {...params} />}
                          />
                        </Box>
                        <DateSeparator>~</DateSeparator>
                        <Box sx={{ flex: 1, '& > *': { width: '100%' } }}>
                          <DatePicker
                            inputFormat="yyyy-MM-dd"
                            value={passExpireValue(lp)}
                            // 회원상세 기간 변경과 같은 제약 — 시작일보다 이른 종료일은 고를 수 없다.
                            // 저장할 때만 막으면 다 입력하고 나서야 되돌려야 한다.
                            minDate={passStartValue(lp) || undefined}
                            onChange={date => setPassEdit(lp, 'expire', date)}
                            renderInput={params => <StyledTextField size="small" {...params} />}
                          />
                        </Box>
                      </LocalizationProvider>
                    </FormInputRow>

                    {/* 기간 입력 바로 아래 보조 동작 — 옆에 끼우면 두 DatePicker 가 눌린다 */}
                    <Box sx={{ display: 'flex', gap: '6px', mt: '8px' }}>
                      <Button
                        onClick={() => setPassStartToday(lp)}
                        sx={{
                          height: 32, px: '12px', borderRadius: '8px',
                          fontSize: 13, fontWeight: 500, color: '#1F3A5F',
                          border: '1px solid #1F3A5F', background: '#fff',
                          '&:hover': { background: '#1F3A5F', color: '#fff' },
                        }}>
                        {t('오늘로 변경')}
                      </Button>
                      <Button
                        onClick={() => resetPassEdit(lp)}
                        disabled={!passEditChanged(lp)}
                        sx={{
                          height: 32, px: '12px', borderRadius: '8px',
                          fontSize: 13, fontWeight: 500, color: '#595959',
                          border: '1px solid #D9D9D9', background: '#fff',
                          '&:hover': { background: '#F5F5F5' },
                          '&.Mui-disabled': { color: '#BFBFBF', borderColor: '#EFEFEF' },
                        }}>
                        {t('초기화')}
                      </Button>
                    </Box>
                    </Box>
                  </FormRow>
                ))}


              {/* 비밀번호 */}
              <FormRow>
                <FormLabel>{t('비밀번호')}</FormLabel>
                <StyledTextField
                  size="small"
                  type="text"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder={t('비밀번호를 입력하세요')}
                />
              </FormRow>

              {/* 메모 */}
              <FormRowLast>
                <FormLabel>{t('메모')}</FormLabel>
                <MemoTextarea
                  minRows={3}
                  value={memo}
                  onChange={e => setMemo(e.target.value)}
                  placeholder={t('메모를 입력하세요')}
                />
              </FormRowLast>
            </ModalContent>

            {/* Footer — 파괴적 액션(사용 종료)은 좌측으로 격리 */}
            <ModalFooter sx={{ alignItems: 'center' }}>
              <EndUsageButton onClick={handleEndUsageClick} disabled={isLoading} sx={{ flex: 'none', height: 44, px: '16px', fontSize: 14 }}>
                {t('사용 종료')}
              </EndUsageButton>
              <Box sx={{ flex: 1 }} />
              <CancelButton onClick={closeModal} sx={{ flex: 'none', minWidth: 100 }}>
                {t('취소')}
              </CancelButton>
              <SubmitButton onClick={handleSubmit(submit)} disabled={isLoading} sx={{ flex: 'none', minWidth: 140 }}>
                {t('저장')}
              </SubmitButton>
            </ModalFooter>
            </>
            )}
          </ModalContainer>
        </Modal>

        {/* 종료 확인 모달 */}
        <ConfirmModal
          open={showEndConfirm}
          onClose={() => setShowEndConfirm(false)}
          onConfirm={handleEndUsageConfirm}
          highlightText="종료"
          highlightColor="#F44336"
          cancelText="닫기"
          confirmText="종료"
          confirmColor="#F44336"
        />

        {/* 종료 완료 모달 */}
        <AlertModal
          open={showEndSuccess}
          onClose={handleEndSuccessClose}
          message="종료가 완료되었습니다."
          buttonText="확인"
          buttonColor="#1F3A5F"
        />

        {/* 락커 이동 모달 */}
        <LockerSelectModal
          memberId={lockerDetailSingle.memberId}
          title={t('자리 변경')}
          confirmText={t('이동하기')}
          open={showMoveModal}
          onClose={() => setShowMoveModal(false)}
          onSelect={handleLockerMove}
          primaryPassId={selectedSpotPass?.memberLockerPassId}
          passOptions={spotPasses}
          initialLocker={{
            lockerId: lockerDetailSingle.lockerId,
            lockerNumber: lockerDetailSingle.lockerNumber,
            lockerAreaId: lockerDetailSingle.lockerAreaId,
          }}
        />

        {/* 본인 자리로 합치기 — 기간이 겹치면 자리 마지막 종료일 +1 부터 이어서 배정 */}
        <PeriodChangePreviewModal
          open={Boolean(periodPreview)}
          onClose={() => setPeriodPreview(null)}
          onConfirm={() => {
            const pending = periodPreview;
            setPeriodPreview(null);
            submit(pending.values, true, pending.entries);
          }}
          items={periodPreview?.items ?? []}
          shifted={periodPreview?.shifted ?? []}
        />

        <LockerAttachDateModal
          open={Boolean(moveAttachLocker)}
          onClose={closeMoveAttach}
          onConfirm={confirmMoveAttach}
          lockerNumber={moveAttachLocker?.lockerNumber}
          spotPasses={
            moveAttachLocker
              ? (assignedMemberProducts?.lockerPasses ?? [])
                  .filter(lp => lp.branchLockerId === moveAttachLocker.lockerId)
                  .map(lp => ({ ...lp, expired: isExpiredPass(lp) }))
              : []
          }
          movingPasses={moveAttachPasses}
        />
      </>
    );
  }

  // 회원이 배정되지 않은 경우 - 배정 모달
  return (
    <Modal open={open} onClose={closeModal} disableEscapeKeyDown>
      <ModalContainer sx={activeTab === 'history' ? HISTORY_MODAL_SX : undefined}>
        {/* Header */}
        <ModalHeader>
          <ModalTitle>{t('라커 배정')}</ModalTitle>
          <StyledCloseIcon onClick={closeModal} />
        </ModalHeader>

        {/* 컨텍스트 밴드 — 수정 불가였던 구역 select·번호 필드를 한 줄로 (디자인 최종안 04) */}
        <Box
          sx={{
            background: '#F5F7FA',
            borderBottom: '1px solid #ECEFF0',
            p: '12px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#262626' }}>
            {lockerAreas.find(area => area.id === lockerArea)?.lockerAreaName || t('라커')} ·{' '}
            <Box component="span" sx={{ color: '#1F3A5F' }}>
              {lockerDetailSingle.lockerNumber}
              {t('번')}
            </Box>
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 12.5, color: '#737373' }}>
            {t('사용 여부')}
            <StyledSwitch checked={isActive} onChange={handleUseYnToggle} />
          </Box>
        </Box>

        {tabBar}

        {activeTab === 'history' ? historyView : (
        <>
        {/* Content — 기존 FormRow 라벨 UI. 구역/번호 필드 제거(컨텍스트 밴드)는 유지 */}
        <ModalContent sx={{ p: '0 24px' }}>
          {/* 회원 입력 */}
          <FormRow>
            <FormLabel>{t('회원 입력')}</FormLabel>
            <ErrorFieldWrapper hasError={errorFields.member}>
              <StyledAutocomplete
                onChange={handleMemberSelect}
                value={memberData}
                disablePortal
                loading={searchLoading}
                noOptionsText={t('검색된 회원이 없습니다')}
                getOptionLabel={option => option?.name || ''}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.name}
                  </li>
                )}
                options={searchResults}
                disabled={!isActive}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder={t('이름 또는 휴대폰 번호')}
                    onInput={e => getMembers(e.target.value)}
                    disabled={!isActive}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.endAdornment}
                          <SearchIcon sx={{ color: '#999' }} />
                        </>
                      ),
                    }}
                  />
                )}
              />
            </ErrorFieldWrapper>
          </FormRow>

          {/* 이용권 선택 - 등록 회원만. 회원이 보유한 미배정 라커 패스 + 고정 '무료 배정' */}
          {memberType === 'registered' && (
            <FormRow>
              <FormLabel>{t('이용권 선택')}</FormLabel>
              <ErrorFieldWrapper hasError={errorFields.ticket}>
                <StyledSelect
                  value={selectedTicket}
                  onChange={e => setSelectedTicket(e.target.value)}
                  displayEmpty
                  size="small"
                  disabled={!isActive}>
                  <MenuItem value="" disabled>
                    <PlaceholderText>{t('이용권을 선택하세요')}</PlaceholderText>
                  </MenuItem>
                  <MenuItem value={FREE_ASSIGN_VALUE}>{t('무료 배정')}</MenuItem>
                  {/* MUI Select 는 children 을 순회하며 props.value 를 읽으므로
                      && 단축평가로 ''/false 가 children 에 섞이면 SelectInput 이 터진다. 반드시 삼항. */}
                  {watchedFormMemberId && unassignedPasses.length === 0 ? (
                    <MenuItem value="__no_pass__" disabled>
                      {t('회원이 보유한 미배정 라커 이용권이 없습니다')}
                    </MenuItem>
                  ) : null}
                  {unassignedPasses.map(lp => (
                    <MenuItem key={lp.memberLockerPassId} value={lp.memberLockerPassId}>
                      {`${lp.productName || t('라커 이용권')} (${lp.startDate} ~ ${lp.expireDate})`}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </ErrorFieldWrapper>
            </FormRow>
          )}

          {/* 무료 배정 상품 선택 — 기간 계산·이력용 지점 라커 상품 (기존 직접배정 플로우) */}
          {memberType === 'registered' && selectedTicket === FREE_ASSIGN_VALUE && (
            <FormRow>
              <FormLabel>{t('상품 선택')}</FormLabel>
              <ErrorFieldWrapper hasError={errorFields.freeProduct}>
                <StyledSelect
                  value={selectedFreeProduct}
                  onChange={e => setSelectedFreeProduct(e.target.value)}
                  displayEmpty
                  size="small"
                  disabled={!isActive}>
                  <MenuItem value="" disabled>
                    <PlaceholderText>{t('상품을 선택하세요')}</PlaceholderText>
                  </MenuItem>
                  {lockerProducts.length === 0 ? (
                    <MenuItem value="__no_product__" disabled>
                      {t('지점에 라커 상품이 없습니다. 상품 관리에서 먼저 등록해주세요.')}
                    </MenuItem>
                  ) : null}
                  {lockerProducts.map(product => {
                    const periodUnit = product.periodType === 'MONTH' ? '개월' : '일';
                    const label = product.periodValue
                      ? `${product.productName} (${product.periodValue}${periodUnit})`
                      : product.productName;
                    return (
                      <MenuItem key={product.productId} value={product.productId}>
                        {label}
                      </MenuItem>
                    );
                  })}
                </StyledSelect>
              </ErrorFieldWrapper>
            </FormRow>
          )}

          {/* 기간 설정 — 이용권/상품 선택 시 자동 입력되고, 그대로 수정도 가능하다.
              보유 패스를 선택한 경우 날짜를 바꾸면 배정 시 기간수정이 함께 저장된다. */}
          <FormRow>
            <FormLabel>{t('기간 설정')}</FormLabel>
            <FormInputRow>
              <ErrorFieldWrapper hasError={errorFields.issueDate}>
                <FormInputDate
                  name="issueDate"
                  control={control}
                  rules={{ required: true }}
                  placeholder="YYYY-MM-DD"
                  value={lockerDetailSingle.issueDate}
                  disabled={!isActive}
                  renderInput={params => (
                    <StyledTextField {...params} error={!!errors.issueDate} disabled={!isActive} />
                  )}
                />
              </ErrorFieldWrapper>
              <DateSeparator>~</DateSeparator>
              <ErrorFieldWrapper hasError={errorFields.expireDate}>
                <FormInputDate
                  name="expireDate"
                  control={control}
                  minDate={watch('issueDate')}
                  placeholder="YYYY-MM-DD"
                  value={lockerDetailSingle.expireDate}
                  disabled={!isActive}
                  renderInput={params => (
                    <StyledTextField {...params} error={!!errors.expireDate} disabled={!isActive} />
                  )}
                />
              </ErrorFieldWrapper>
            </FormInputRow>
          </FormRow>

          {/* 자물쇠 번호 */}
          <FormRow>
            <FormLabel>{t('비밀번호')}</FormLabel>
            <StyledTextField
              size="small"
              type="text"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder={t('비밀번호를 입력하세요')}
              disabled={!isActive}
            />
          </FormRow>

          {/* 메모 */}
          <FormRowLast>
            <FormLabel>{t('메모')}</FormLabel>
            <MemoTextarea
              minRows={1}
              value={memo}
              onChange={e => setMemo(e.target.value)}
              placeholder={t('메모를 입력하세요')}
              disabled={!isActive}
            />
          </FormRowLast>
        </ModalContent>

        {/* Footer — 대상·결과를 버튼에 명시 */}
        <ModalFooterAssign>
          <CommonModalButton variant="cancel" onClick={closeModal}>
            {t('취소')}
          </CommonModalButton>
          <CommonModalButton
            variant="submit"
            onClick={handleSubmit(submit)}
            disabled={isLoading}
            sx={{ backgroundColor: '#1F3A5F', '&:hover': { backgroundColor: '#172C48' } }}>
            {t('확인')}
          </CommonModalButton>
        </ModalFooterAssign>
        </>
        )}
      </ModalContainer>
    </Modal>
  );
};

export default LockerUpdateModal;
